/**
 * useQuestionManager Hook
 * 
 * Manages question selection, filtering, and shuffling logic with intelligent
 * question prioritization to avoid recent repeats while ensuring variety.
 * 
 * @module useQuestionManager
 */

import { useMemo, useCallback, useRef } from 'react';
import { ONE_DAY_MS } from '../constants/timing';

/**
 * Hook for managing question selection and shuffling with smart prioritization.
 * 
 * Prioritizes questions in this order:
 * 1. New questions (never asked)
 * 2. Old questions (asked >24 hours ago)
 * 3. Recent questions (asked <24 hours ago)
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.questionSets - Available question sets by subject key (e.g., { phonics: [...], math: [...] })
 * @param {string} params.currentScreen - Current subject identifier ('phonics', 'math', 'reading', etc.)
 * @param {number} params.questionCount - Number of questions to select for the current game
 * @param {Object} params.progress - Progress object containing questionHistory
 * @param {Object} [params.progress.questionHistory] - Nested object: { subject: { questionId: timestamp } }
 * @param {Function} params.getQuestionId - Function to generate unique question identifiers
 * @param {Object} [params.additionalSets={}] - Additional question sets (e.g., { social: [...], skipcounting: [...] })
 * @param {number} [params.currentQuestion=0] - Current question index (0 indicates a new game started)
 * @returns {Object} Question management functions and current questions
 * @returns {Array} returns.fullQuestionSet - Complete question array for current subject
 * @returns {Array} returns.currentQuestions - Selected and shuffled questions for current game
 * @returns {Function} returns.buildQuestionList - Function to build question list from source
 * @returns {Function} returns.smartShuffle - Function to intelligently shuffle questions based on history
 * 
 * @example
 * const { currentQuestions, smartShuffle } = useQuestionManager({
 *   questionSets: { math: mathQuestions, phonics: phonicsQuestions },
 *   currentScreen: 'math',
 *   questionCount: 10,
 *   progress: { questionHistory: { math: { 'q1': Date.now() } } },
 *   getQuestionId: (q) => q.id
 * });
 * 
 * // currentQuestions will contain 10 questions, prioritizing never-asked questions
 */
export const useQuestionManager = ({
  questionSets,
  currentScreen,
  questionCount,
  progress,
  getQuestionId,
  additionalSets = {},
  currentQuestion = 0
}) => {
  // Get full question set for current screen
  const fullQuestionSet = useMemo(() => {
    switch (currentScreen) {
      case 'phonics':
        return questionSets.phonics || [];
      case 'math':
        return questionSets.math || [];
      case 'reading':
        return questionSets.reading || [];
      case 'science':
        return questionSets.science || [];
      case 'social':
        return additionalSets.social || [];
      case 'skipcounting':
        return additionalSets.skipcounting || [];
      case 'art':
        return questionSets.art || [];
      case 'geography':
        return questionSets.geography || [];
      case 'history':
        return questionSets.history || [];
      default:
        return [];
    }
  }, [currentScreen, questionSets, additionalSets]);

  /**
   * Intelligently shuffles questions, prioritizing new questions over recently asked ones.
   * Questions are categorized and shuffled within categories to maintain randomness.
   * 
   * @param {Array} questions - Array of question objects to shuffle
   * @param {string} subject - Subject identifier for looking up question history
   * @param {number} count - Maximum number of questions to return
   * @returns {Array} Shuffled array of questions, prioritizing new questions
   * 
   * @example
   * const shuffled = smartShuffle(mathQuestions, 'math', 10);
   * // Returns 10 questions, prioritizing never-asked questions
   */
  const smartShuffle = useCallback((questions, subject, count) => {
    const currentTime = Date.now();
    const oneDay = ONE_DAY_MS;
    
    // Get question history for this subject with null check
    const subjectHistory = (progress?.questionHistory && progress.questionHistory[subject]) || {};
    
    // Separate questions into categories
    const recentQuestions = []; // Asked within last 24 hours
    const oldQuestions = []; // Asked more than 24 hours ago
    const newQuestions = []; // Never asked
    
    questions.forEach((question, index) => {
      const questionId = getQuestionId(question);
      const lastAsked = subjectHistory[questionId];
      
      if (!lastAsked) {
        newQuestions.push({ question, index });
      } else if (currentTime - lastAsked < oneDay) {
        recentQuestions.push({ question, index });
      } else {
        oldQuestions.push({ question, index });
      }
    });
    
    // Shuffle within each category to maintain some randomness
    const shuffleCategory = (category) => {
      const shuffled = [...category];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    };
    
    const shuffledNew = shuffleCategory(newQuestions);
    const shuffledOld = shuffleCategory(oldQuestions);
    const shuffledRecent = shuffleCategory(recentQuestions);
    
    // Combine with priority: new > old > recent
    const finalQuestions = [
      ...shuffledNew,
      ...shuffledOld,
      ...shuffledRecent
    ];
    
    // Take only the requested number of questions
    return finalQuestions.slice(0, count).map(item => item.question);
  }, [progress?.questionHistory, getQuestionId]);

  /**
   * Builds a question list from a source array with optional shuffling.
   * 
   * @param {Array} source - Source array of questions
   * @param {string} screen - Subject/screen identifier
   * @param {number} count - Number of questions to return
   * @param {boolean} [shuffle=false] - Whether to apply smart shuffling
   * @returns {Array} Array of questions (shuffled if requested)
   * 
   * @example
   * const questions = buildQuestionList(phonicsQuestions, 'phonics', 10, true);
   */
  const buildQuestionList = useCallback((source, screen, count, shuffle = false) => {
    let base = Array.isArray(source) ? source.slice(0) : [];
    if (base.length === 0 && questionSets && questionSets[screen]) {
      base = questionSets[screen].slice(0);
    }
    const safeCount = Number.isFinite(count) && count > 0 ? count : 10;
    
    if (!shuffle) {
      return base.slice(0, safeCount);
    }
    
    return smartShuffle(base, screen, safeCount);
  }, [questionSets, smartShuffle]);

  // Memoize current questions to prevent unnecessary recalculations
  // Use refs to track game state and detect new games
  const lastScreenRef = useRef(currentScreen);
  const lastQuestionCountRef = useRef(questionCount);
  const lastCurrentQuestionRef = useRef(currentQuestion);
  const questionsCacheRef = useRef(null);
  
  const currentQuestions = useMemo(() => {
    const screenChanged = lastScreenRef.current !== currentScreen;
    const countChanged = lastQuestionCountRef.current !== questionCount;
    const questionReset = currentQuestion === 0 && lastCurrentQuestionRef.current > 0;
    
    // Detect new game: screen/count changed OR question reset to 0 from a higher value
    const isNewGame = screenChanged || countChanged || questionReset || !questionsCacheRef.current;
    
    if (isNewGame && currentQuestion === 0) {
      // Starting a new game - regenerate and shuffle questions
      lastScreenRef.current = currentScreen;
      lastQuestionCountRef.current = questionCount;
      lastCurrentQuestionRef.current = currentQuestion;
      
      const SHUFFLE_QUESTIONS = true; // Enable randomization for variety
      const newQuestions = buildQuestionList(fullQuestionSet, currentScreen, questionCount, SHUFFLE_QUESTIONS);
      questionsCacheRef.current = newQuestions;
      return newQuestions;
    }
    
    // Update refs to track progress
    lastCurrentQuestionRef.current = currentQuestion;
    
    // Return cached questions during gameplay (mid-game)
    if (questionsCacheRef.current && currentQuestion > 0) {
      return questionsCacheRef.current;
    }
    
    // Fallback: generate questions if cache is empty
    if (!questionsCacheRef.current) {
      const newQuestions = buildQuestionList(fullQuestionSet, currentScreen, questionCount, true);
      questionsCacheRef.current = newQuestions;
      return newQuestions;
    }
    
    return questionsCacheRef.current;
  }, [fullQuestionSet, currentScreen, questionCount, currentQuestion, buildQuestionList]);

  return {
    fullQuestionSet,
    currentQuestions,
    buildQuestionList,
    smartShuffle
  };
};

